import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaUsers, 
  FaPlus, 
  FaComment, 
  FaThumbtack, 
  FaFire, 
  FaSearch, 
  FaFilter,
  FaUserCircle,
  FaHeart,
  FaShare,
  FaCalendarAlt,
  FaLightbulb,
  FaBullhorn,
  FaHandsHelping,
  FaTimes,
  FaTrash,
  FaEdit,
  FaRegClock,
  FaChevronRight
} from "react-icons/fa";
import { SiGooglescholar } from "react-icons/si";
import { communityAPI } from "../../services/api"; // IMPORT THE API
import HeroSection from "../../components/HeroSection";

const Community = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [newComment, setNewComment] = useState({});
  const [activePost, setActivePost] = useState(null);
  const [error, setError] = useState("");

  // Categories with icons and colors
  const categories = [
    { id: "all", name: "All Topics", icon: <FaUsers />, color: "from-blue-500 to-cyan-500" },
    { id: "general", name: "General", icon: <FaUsers />, color: "from-gray-500 to-gray-700" },
    { id: "events", name: "Events", icon: <FaCalendarAlt />, color: "from-emerald-500 to-green-600" },
    { id: "help", name: "Help & Support", icon: <FaHandsHelping />, color: "from-amber-500 to-orange-500" },
    { id: "ideas", name: "Ideas & Suggestions", icon: <FaLightbulb />, color: "from-purple-500 to-pink-500" },
    { id: "announcements", name: "Announcements", icon: <FaBullhorn />, color: "from-red-500 to-rose-600" },
    { id: "cultural", name: "Cultural", icon: <SiGooglescholar />, color: "from-primary-500 to-primary-600" },
  ];

  // Fetch posts from backend API
  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, sortBy, searchQuery]);

  const fetchPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const filters = {
        category: selectedCategory,
        sort: sortBy,
        search: searchQuery,
        page: 1,
        limit: 20
      };
      
      const response = await communityAPI.getPosts(filters);
      
      if (response.success) {
        // Transform backend data to frontend format
        const transformedPosts = response.data.map(post => ({
          id: post.id,
          title: post.title,
          content: post.content,
          author: post.author_name || post.username,
          authorRole: post.author_role || "Member",
          authorAvatar: post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.username}`,
          category: post.category,
          created_at: post.created_at,
          is_pinned: post.is_pinned,
          likes: post.likes || 0,
          comments_count: post.comments_count || 0,
          tags: post.tags || [],
          attachments: post.attachments || []
        }));
        
        setPosts(transformedPosts);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setError("Failed to load community posts. Please try again.");
      // Fallback to mock data if API fails
      setPosts(getMockPosts());
    } finally {
      setLoading(false);
    }
  };

  // Mock data fallback
  const getMockPosts = () => {
    return [
      {
        id: 1,
        title: "Annual Ojoto Union Picnic 2024 - Save the Date!",
        content: "Mark your calendars! Our annual community picnic will be held on July 20th at Central Park. Family-friendly event with food, games, and cultural activities.",
        author: "Chinwe Okafor",
        authorRole: "Event Coordinator",
        authorAvatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Chinwe",
        category: "events",
        created_at: "2024-12-10T14:30:00",
        is_pinned: true,
        likes: 24,
        comments: []
      },
      {
        id: 2,
        title: "Looking for Housing Assistance in Toronto",
        content: "Moving to Toronto next month and looking for temporary accommodation. Any recommendations for short-term rentals or community members who can help?",
        author: "Tunde Adeyemi",
        authorRole: "Member",
        authorAvatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Tunde",
        category: "help",
        created_at: "2024-12-09T11:20:00",
        is_pinned: false,
        likes: 15,
        comments: []
      }
    ];
  };

  const filteredPosts = posts.filter(post => {
    // Category filter (frontend fallback)
    if (selectedCategory !== "all" && post.category !== selectedCategory) {
      return false;
    }
    
    // Search filter (frontend fallback)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        post.title.toLowerCase().includes(query) ||
        post.content.toLowerCase().includes(query) ||
        post.author.toLowerCase().includes(query)
      );
    }
    
    return true;
  }).sort((a, b) => {
    // Sorting (frontend fallback)
    switch (sortBy) {
      case "newest":
        return new Date(b.created_at) - new Date(a.created_at);
      case "most_commented":
        return (b.comments_count || 0) - (a.comments_count || 0);
      case "most_liked":
        return (b.likes || 0) - (a.likes || 0);
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  const handleLikePost = async (postId) => {
    try {
      await communityAPI.likePost(postId);
      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, likes: (post.likes || 0) + 1 } : post
      ));
    } catch (error) {
      console.error('Failed to like post:', error);
      alert("Failed to like post. Please try again.");
    }
  };

  const handleAddComment = async (postId) => {
    if (!newComment[postId]?.trim()) return;
    
    try {
      const response = await communityAPI.addComment(postId, newComment[postId].trim());
      
      if (response.success) {
        // Update local state
        const comment = response.data;
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                comments_count: (post.comments_count || 0) + 1,
                // If you want to show comments immediately, fetch them
                // Or you could fetch comments for this post
              } 
            : post
        ));
        
        setNewComment(prev => ({ ...prev, [postId]: "" }));
        
        // Refresh comments for active post
        if (activePost === postId) {
          // You could fetch comments here or rely on users to refresh
          alert("Comment added successfully!");
        }
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert("Failed to add comment. Please try again.");
    }
  };

  const handleDeletePost = (postId) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      // TODO: Add API call for delete when backend supports it
      setPosts(prev => prev.filter(post => post.id !== postId));
      alert("Post deleted (frontend only - backend delete not implemented yet)");
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <HeroSection
        title={["Community Forum.", "Connect. Share. Engage."]}
        subtitle="Connect, share ideas, and engage with the Ojoto Union community. Join discussions, get help, and stay updated with community events."
        ctaText="CREATE POST"
        ctaLink="/community/create"
        showCarousel={true}
      />
      
      {/* Action Buttons and Stats Below Hero */}
      <div className="bg-white shadow-md py-4 px-6 -mt-16 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/community/create"
                className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                <FaPlus />
                Create Post
              </Link>
              
              <button 
                onClick={fetchPosts}
                className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition"
              >
                <FaFilter />
                Refresh
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-gray-900 mb-1">{posts.length}</div>
              <div className="text-gray-600 text-sm">Total Posts</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {posts.reduce((acc, post) => acc + (post.comments_count || 0), 0)}
              </div>
              <div className="text-gray-600 text-sm">Total Comments</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {posts.reduce((acc, post) => acc + (post.likes || 0), 0)}
              </div>
              <div className="text-gray-600 text-sm">Total Likes</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {new Set(posts.map(post => post.author)).size}
              </div>
              <div className="text-gray-600 text-sm">Active Members</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error} <button onClick={fetchPosts} className="underline ml-2">Try again</button>
          </div>
        )}

        {/* Categories Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Discussion Categories</h3>
            <button 
              onClick={() => setSelectedCategory("all")}
              className="text-sm text-primary-600 hover:text-gray-900 font-medium"
            >
              Clear Filter
            </button>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                  selectedCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="text-lg">{category.icon}</span>
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts, topics, or members..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition"
              />
            </div>

            {/* Sort */}
            <div className="relative">
              <FaFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full pl-12 pr-8 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent appearance-none bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="most_commented">Most Comments</option>
                <option value="most_liked">Most Liked</option>
              </select>
            </div>

            {/* Active Filters */}
            <div className="flex items-center gap-2">
              {(selectedCategory !== "all" || searchQuery) && (
                <div className="flex flex-wrap gap-2">
                  {selectedCategory !== "all" && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {getCategoryInfo(selectedCategory).name}
                      <button onClick={() => setSelectedCategory("all")}>
                        <FaTimes size={12} />
                      </button>
                    </span>
                  )}
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery("")}>
                        <FaTimes size={12} />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading community posts...</p>
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="space-y-6">
            {/* Pinned Posts First */}
            {filteredPosts.filter(post => post.is_pinned).map(post => (
              <PostCard
                key={post.id}
                post={post}
                categoryInfo={getCategoryInfo(post.category)}
                formatTimeAgo={formatTimeAgo}
                onLike={handleLikePost}
                onComment={handleAddComment}
                onDelete={handleDeletePost}
                newComment={newComment}
                setNewComment={setNewComment}
                activePost={activePost}
                setActivePost={setActivePost}
              />
            ))}
            
            {/* Regular Posts */}
            {filteredPosts.filter(post => !post.is_pinned).map(post => (
              <PostCard
                key={post.id}
                post={post}
                categoryInfo={getCategoryInfo(post.category)}
                formatTimeAgo={formatTimeAgo}
                onLike={handleLikePost}
                onComment={handleAddComment}
                onDelete={handleDeletePost}
                newComment={newComment}
                setNewComment={setNewComment}
                activePost={activePost}
                setActivePost={setActivePost}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <FaUsers className="text-4xl text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">No Posts Found</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              {searchQuery 
                ? `No posts match "${searchQuery}". Try a different search term.`
                : `Be the first to start a discussion in the ${selectedCategory !== "all" ? getCategoryInfo(selectedCategory).name.toLowerCase() : "community"}!`
              }
            </p>
            <Link
              to="/community/create"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-gray-900 font-bold rounded-xl hover:shadow-lg transition"
            >
              <FaPlus />
              Create First Post
            </Link>
          </div>
        )}

        {/* Community Guidelines */}
        <div className="mt-12 p-8 bg-gradient-to-r from-gray-900/5 to-primary-700/5 rounded-2xl border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 font-['Playfair_Display',_serif]">
            Community Guidelines
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-4 bg-white rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-3">
                <FaUsers className="text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Be Respectful</h4>
              <p className="text-gray-600 text-sm">Treat all members with respect and courtesy.</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 mb-3">
                <FaHandsHelping className="text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Help Others</h4>
              <p className="text-gray-600 text-sm">Share knowledge and support fellow members.</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 mb-3">
                <FaLightbulb className="text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Share Ideas</h4>
              <p className="text-gray-600 text-sm">Contribute constructive ideas and feedback.</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-3">
                <FaBullhorn className="text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Stay Relevant</h4>
              <p className="text-gray-600 text-sm">Keep discussions focused on community topics.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Post Card Component (Updated to work with real data)
const PostCard = ({ 
  post, 
  categoryInfo, 
  formatTimeAgo, 
  onLike, 
  onComment, 
  onDelete, 
  newComment, 
  setNewComment,
  activePost,
  setActivePost 
}) => {
  const isActive = activePost === post.id;

  return (
    <div className={`bg-white rounded-2xl shadow-lg border ${post.is_pinned ? 'border-amber-300 border-l-4 border-l-amber-500' : 'border-gray-200'} overflow-hidden transition-all duration-300 hover:shadow-xl`}>
      {/* Post Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-start gap-4">
            <img 
              src={post.authorAvatar} 
              alt={post.author}
              className="w-12 h-12 rounded-full border-2 border-gray-200"
            />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-gray-800">{post.author}</h3>
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                  {post.authorRole}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <FaRegClock />
                  {formatTimeAgo(post.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <FaComment />
                  {post.comments_count || 0} comments
                </span>
                <span className="flex items-center gap-1">
                  <FaHeart />
                  {post.likes || 0} likes
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {post.is_pinned && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                <FaThumbtack />
                Pinned
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-white text-sm font-medium bg-gradient-to-r ${categoryInfo.color}`}>
              {categoryInfo.name}
            </span>
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h2>
        <p className="text-gray-700 leading-relaxed">{post.content}</p>
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {post.tags.map((tag, index) => (
              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Post Actions */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button
              onClick={() => onLike(post.id)}
              className="flex items-center gap-2 text-gray-600 hover:text-red-500 transition-colors group"
            >
              <FaHeart className={`group-hover:scale-110 transition-transform ${post.likes > 0 ? 'text-red-500' : ''}`} />
              <span className="font-medium">{post.likes || 0}</span>
            </button>
            
            <button
              onClick={() => setActivePost(isActive ? null : post.id)}
              className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors"
            >
              <FaComment />
              <span className="font-medium">Comment</span>
            </button>
            
            <button className="flex items-center gap-2 text-gray-600 hover:text-blue-500 transition-colors">
              <FaShare />
              <span className="font-medium">Share</span>
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Only show delete for author/admin - TODO: Implement proper auth check */}
            {/* {post.author === "Current User" && (
              <button
                onClick={() => onDelete(post.id)}
                className="flex items-center gap-2 text-gray-500 hover:text-red-600 transition-colors"
              >
                <FaTrash />
              </button>
            )} */}
          </div>
        </div>
      </div>

      {/* Comments Section */}
      {isActive && (
        <div className="p-6 bg-gray-50 border-t border-gray-100">
          {/* Note about comments */}
          <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-blue-700 text-sm">
              💡 Comments are saved to the database. Refresh the page to see new comments from other users.
            </p>
          </div>

          {/* Add Comment Form */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h5 className="font-semibold text-gray-800 mb-3">Add your comment</h5>
            <div className="flex gap-3">
              <textarea
                value={newComment[post.id] || ""}
                onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                placeholder="Write your comment here..."
                className="flex-1 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition"
                rows="2"
              />
              <button
                onClick={() => onComment(post.id)}
                disabled={!newComment[post.id]?.trim()}
                className={`px-6 py-3 rounded-xl font-semibold transition ${
                  newComment[post.id]?.trim()
                    ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-gray-900 hover:shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                Post
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              Your comment will be visible to all community members.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;